import { Directive, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";

/**
 * Usage:
 * <div [appScrollLock]="isDropdownOpen">...</div>
 */
@Directive({
  selector: "[appScrollLock]",
})
export class ScrollLockDirective implements OnChanges, OnDestroy {
  @Input() appScrollLock: boolean = false;

  private scrollContainerSelector = "#page-scroll";
  private isContributingToLock = false;

  // Every ScrollLockDirective instance resolves the same global container (see
  // scrollContainerSelector) regardless of which element it's bound to, so two instances
  // locking at once (#4405) must compose rather than clobber each other: only the first lock
  // measures/applies, and the DOM lock is only released once every contributor has released.
  private static lockCount = 0;
  // The container's own inline padding-right/overflow at the moment of the first lock, restored
  // on the last release instead of being blanked to "" (#4405). No consumer sets either today,
  // so this is a no-op restore in practice, but it's cheap and stops a future inline value from
  // being silently wiped by an unrelated scroll lock/unlock.
  private static originalPaddingRight: string | null = null;
  private static originalOverflow: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["appScrollLock"]) {
      this.updateScrollLock();
    }
  }

  ngOnDestroy(): void {
    this.releaseLock();
  }

  private updateScrollLock(): void {
    if (this.appScrollLock) {
      this.acquireLock();
    } else {
      this.releaseLock();
    }
  }

  private acquireLock(): void {
    if (this.isContributingToLock) {
      return;
    }
    this.isContributingToLock = true;
    ScrollLockDirective.lockCount++;
    if (ScrollLockDirective.lockCount > 1) {
      // Already locked by another instance — its measurement/compensation still applies.
      return;
    }

    const scrollContainer = document.querySelector(this.scrollContainerSelector) as HTMLElement;
    if (scrollContainer) {
      // overflow:hidden removes the scrollbar (if one was showing), which reclaims its
      // width and shifts the page underneath — measure it beforehand and compensate with
      // padding-right (box-sizing: border-box makes this a like-for-like swap) so locking
      // scroll never visibly shifts the layout.
      //
      // offsetWidth - clientWidth is border-left + border-right + scrollbar, not just the
      // scrollbar (#4405). It's pure scrollbar today only because .auto-hide-scrollbar
      // (base.scss) and this container declare no borders — if either ever gains one, the
      // padding compensation below over-pads by the border width.
      const scrollbarWidth = scrollContainer.offsetWidth - scrollContainer.clientWidth;
      const originalPaddingRight = scrollContainer.style.paddingRight;
      ScrollLockDirective.originalPaddingRight = originalPaddingRight;
      ScrollLockDirective.originalOverflow = scrollContainer.style.overflow;
      scrollContainer.style.overflow = "hidden";
      scrollContainer.style.paddingRight =
        scrollbarWidth > 0
          ? `${parseFloat(originalPaddingRight || "0") + scrollbarWidth}px`
          : originalPaddingRight;
      // Explicit signal for ElasticScrollDirective / PullToRefreshDirective — decoupled from
      // the overflow mechanism so those directives survive future lock-strategy changes.
      scrollContainer.setAttribute("data-scroll-locked", "true");
    }
  }

  private releaseLock(): void {
    if (!this.isContributingToLock) {
      return;
    }
    this.isContributingToLock = false;
    ScrollLockDirective.lockCount = Math.max(0, ScrollLockDirective.lockCount - 1);
    if (ScrollLockDirective.lockCount > 0) {
      // Another instance still wants the lock — leave the container as-is.
      return;
    }

    const scrollContainer = document.querySelector(this.scrollContainerSelector) as HTMLElement;
    if (scrollContainer) {
      scrollContainer.style.overflow = ScrollLockDirective.originalOverflow ?? "";
      scrollContainer.style.paddingRight = ScrollLockDirective.originalPaddingRight ?? "";
      scrollContainer.removeAttribute("data-scroll-locked");
    }
    ScrollLockDirective.originalPaddingRight = null;
    ScrollLockDirective.originalOverflow = null;
  }
}
